import { useCallback, useMemo, useRef, useEffect } from 'react'
import { PublicClientApplication, Configuration } from '@azure/msal-browser'
import {
  useHideModal,
  useSetActiveModal,
  useIsShowModal,
} from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  SetLinkRequest,
  LinkDoc,
} from '@trustero/trustero-api-web/lib/linker/linker_pb'
import { LinkerPromiseClient } from '@trustero/trustero-api-web/lib/linker/linker_grpc_web_pb'
import { useAuthorizedGrpcClientWithContentUpdate } from 'src/adapter/grpcClient'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { useUser } from 'src/components/async/Users'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useFolderScanReceptor } from 'src/pages/Receptors/receptors.hooks'
import {
  blockIfReceptorNeedsCredentials,
  credentialsActionForPickerSource,
} from 'src/pages/Receptors/folderScanReceptor.helpers'
import { Source } from '../../Linkers.constants'
import { useSharepointOptionsHelper } from './SharepointFilePickerModal.helpers'
import {
  SharepointReceptorCredentials,
  SharepointFile,
} from './SharepointFilePickerModal.types'
import { useSharepointFilePickerContext } from './SharepointFilePickerContext'

export const useSharepointLinker = (): ((
  receptor: ReceptorRecord.AsObject,
  files: SharepointFile[],
  isViaRequest: boolean,
  caption: string,
) => Promise<string[]>) => {
  const linkerClient =
    useAuthorizedGrpcClientWithContentUpdate(LinkerPromiseClient)
  const { data: user } = useUser()
  const userId = useMemo(() => user?.getId() || '', [user])
  const linkDocuments = useCallback(
    async (
      receptor: ReceptorRecord.AsObject,
      files: SharepointFile[],
      isViaRequest: boolean,
      caption: string,
    ) => {
      const request = new SetLinkRequest()
      request.setReceptorId(receptor.id)
      const linkDocs = files.map((file: SharepointFile) => {
        const importKeyParts = file.documentLibraryId
          ? [file.siteId, file.documentLibraryId, file.id, file.type]
          : [file.siteId, file.id, file.type]
        const linkDoc = new LinkDoc()
          .setImportKey(importKeyParts.join(':'))
          .setImportLink(file.webUrl)
          .setDocumentType(
            file.type === 'Evidence'
              ? DOCUMENT_TYPE.EVIDENCE
              : DOCUMENT_TYPE.POLICYDOC,
          )
          .setSubjectIdsList(file.subjectIds)
          .setCaption(caption && caption.length > 0 ? caption : file.display)
          .setFileName(file.display)
          .setIsViaRequest(isViaRequest)
          .setRelevantDate(file.relevantDate || undefined)
          .setReceptorModelId(receptor.modelid)
          .setUserId(userId)
        return linkDoc
      })
      request.setDocumentsList(linkDocs)
      const response = await linkerClient.setLink(request)
      return response.getEvidenceIdsList()
    },
    [linkerClient, userId],
  )

  return linkDocuments
}

export const useSharepointLinkerShareUrl = (): ((
  shareUrl: string,
  docType: DOCUMENT_TYPE,
  isViaRequest: boolean,
  caption: string,
  subjectIds: Array<string>,
) => Promise<string[]>) => {
  const linkerClient =
    useAuthorizedGrpcClientWithContentUpdate(LinkerPromiseClient)
  const { data: user } = useUser()
  const userId = useMemo(() => user?.getId() || '', [user])
  const receptor = useFolderScanReceptor('trr-sharepoint')

  const linkDocuments = useCallback(
    async (
      shareUrl: string,
      docType: DOCUMENT_TYPE,
      isViaRequest: boolean,
      caption: string,
      subjectIds: Array<string>,
    ) => {
      if (!receptor) {
        throw new Error('SharePoint receptor not found')
      }
      const request = new SetLinkRequest()
      request.setReceptorId(receptor.getId())
      const linkDoc = new LinkDoc()
        .setImportKey(
          `${encodeShareUrl(shareUrl)}:${
            docType === DOCUMENT_TYPE.EVIDENCE ? 'Evidence' : 'Policy'
          }`,
        )
        .setImportLink(shareUrl)
        .setDocumentType(docType)
        .setCaption(caption)
        .setFileName(caption)
        .setIsViaRequest(isViaRequest)
        .setReceptorModelId(receptor.getModelid())
        .setUserId(userId)
        .setSubjectIdsList(subjectIds)
      request.setDocumentsList([linkDoc])
      const response = await linkerClient.setLink(request)
      return response.getEvidenceIdsList()
    },
    [linkerClient, userId, receptor],
  )

  return linkDocuments
}

export const useIsValidSharepointUrl = (): ((url: string) => boolean) => {
  const receptor = useFolderScanReceptor('trr-sharepoint')

  const spCredentials = useMemo(() => {
    return JSON.parse(receptor?.getCredentials() || '{}')
  }, [receptor])

  return useCallback(
    (url: string): boolean => {
      return url.startsWith(spCredentials?.baseurl || 'https://')
    },
    [spCredentials],
  )
}

const encodeShareUrl = (url: string): string => {
  let encodedUrl = btoa(url)
  encodedUrl = encodedUrl.replace(/\+/g, '-').replace(/\//g, '_')
  encodedUrl = encodedUrl.replace(/=+$/, '')
  return 'u!' + encodedUrl
}

// Extracted a utility function to handle port cleanup and modal closure
const cleanupPortAndCloseModal = (
  port: React.MutableRefObject<MessagePort | null>,
  closeModal: () => void,
  messageListener: (message: MessageEvent) => Promise<void>,
  setLoading: (value: boolean) => void,
) => {
  if (port.current) {
    port.current.removeEventListener('message', messageListener)
    port.current.close()
    port.current = null
  }
  setLoading(true)
  closeModal()
}

export const useInitializeSharepointPicker = ({
  source,
  subjectId,
  modalId,
}: {
  source: string
  subjectId?: string
  modalId?: ModalFormId
}): {
  initializePicker: () => Promise<void>
  initializeMessageListener: (event: MessageEvent) => void
  closeSPModal: () => void
} => {
  const parentModal =
    source === 'Evidence'
      ? !subjectId
        ? ModalFormId.ADD_EVIDENCE_GROUPS
        : ModalFormId.ADD_EVIDENCE
      : source === 'FolderScan'
      ? ModalFormId.ADD_FOLDER_SCAN
      : ModalFormId.ADD_DOCUMENT
  const showParentModal = useSetActiveModal(parentModal)
  const {
    setLoading,
    setFilesSelected,
    setSource,
    setSubjectIds,
    selectedDocumentLibrary,
    receptor: contextReceptor,
  } = useSharepointFilePickerContext()
  // The opener puts the connection the user clicked into context; when it did
  // not, fall back to the healthiest SharePoint row rather than an arbitrary one.
  const fallbackReceptor = useFolderScanReceptor('trr-sharepoint')
  const receptor = contextReceptor ?? fallbackReceptor

  const isModalVisible = useIsShowModal(
    modalId || ModalFormId.SHAREPOINT_FILE_PICKER,
  )

  useEffect(() => {
    if (isModalVisible) {
      setSource(source as Source)
      setSubjectIds(subjectId ? [subjectId] : [])
    }
  }, [source, subjectId, setSource, setSubjectIds, isModalVisible])

  const port = useRef<MessagePort | null>(null)
  const closeModal = useHideModal({
    modalId: modalId || ModalFormId.SHAREPOINT_FILE_PICKER,
  })
  const { options, optionsQueryString } = useSharepointOptionsHelper(
    selectedDocumentLibrary,
    source === 'FolderScan' ? 'folders' : 'files',
  )

  const credentials = useMemo((): SharepointReceptorCredentials => {
    if (!receptor || !receptor.getCredentials()) {
      return {} as SharepointReceptorCredentials
    }
    try {
      return JSON.parse(receptor.getCredentials())
    } catch {
      return {} as SharepointReceptorCredentials
    }
  }, [receptor])

  const fetchToken = useCallback(async (): Promise<string> => {
    if (!receptor) throw new Error('Receptor is not available')
    if (
      blockIfReceptorNeedsCredentials(
        receptor,
        receptor.getName() || 'SharePoint',
        credentialsActionForPickerSource(source),
      )
    ) {
      closeModal()
      return ''
    }
    if (!credentials.baseurl) {
      showInfoToast(
        'SharePoint base URL is not configured. Please update the receptor credentials.',
      )
      closeModal()
      return ''
    }

    const msalConfig: Configuration = {
      auth: {
        authority: `https://login.microsoftonline.com/${credentials.tenantid}`,
        clientId: credentials.clientid,
        redirectUri: window.location.origin,
      },
    }

    const app = new PublicClientApplication(msalConfig)
    const rawBaseUrl = credentials.baseurl.replace(/\/$/, '')
    const normalizedBaseUrl = /^https?:\/\//i.test(rawBaseUrl)
      ? rawBaseUrl
      : `https://${rawBaseUrl}`
    const authParams = {
      scopes: [`${normalizedBaseUrl}/.default`],
    }

    try {
      const response = await app.acquireTokenSilent(authParams)
      return response.accessToken
    } catch {
      try {
        const loginResponse = await app.loginPopup(authParams)
        app.setActiveAccount(loginResponse.account)
        const tokenResponse = await app.acquireTokenSilent(authParams)
        return tokenResponse.accessToken
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err)
        showInfoToast(`Authentication canceled or failed: ${errorMessage}`)
        closeModal()
        return ''
      }
    }
  }, [receptor, credentials, closeModal, source])

  const messageListener: (message: MessageEvent) => Promise<void> = useCallback(
    async (message: MessageEvent) => {
      if (!port) return
      switch (message.data.type) {
        case 'notification':
          break
        case 'command': {
          port.current?.postMessage({
            type: 'acknowledge',
            id: message.data.id,
          })

          const command = message.data.data

          switch (command.command) {
            case 'authenticate': {
              const token = await fetchToken()

              if (token) {
                port.current?.postMessage({
                  type: 'result',
                  id: message.data.id,
                  data: {
                    result: 'token',
                    token,
                  },
                })
              }
              break
            }
            case 'close': {
              cleanupPortAndCloseModal(
                port,
                closeModal,
                messageListener,
                setLoading,
              )
              break
            }
            case 'pick': {
              if (!receptor) break
              setFilesSelected(command.items)
              showParentModal()
              break
            }
            default:
              postMessage({
                result: 'error',
                error: {
                  code: 'unsupportedCommand',
                  message: command.command,
                },
                isExpected: true,
              })
              break
          }
          break
        }
      }
    },
    [
      fetchToken,
      receptor,
      closeModal,
      setFilesSelected,
      setLoading,
      showParentModal,
    ],
  )

  const initializeMessageListener = useCallback(
    (event: MessageEvent) => {
      if (event.source) {
        const message = event.data
        if (
          message.type === 'initialize' &&
          message.channelId === options.messaging.channelId
        ) {
          port.current = event.ports[0]
          port.current.addEventListener('message', messageListener)
          port.current.start()
          port.current.postMessage({
            type: 'activate',
          })
          window.removeEventListener('message', initializeMessageListener)
        }
      }
    },
    [messageListener, options],
  )

  const initializePicker = useCallback(async () => {
    const frame = document.getElementById('form-container') as HTMLDivElement
    if (!frame) return
    frame.innerHTML = ''

    const form = document.createElement('form')
    const authToken = await fetchToken()
    if (!authToken) return

    if (!credentials.baseurl) return
    const rawPickerBase = credentials.baseurl.replace(/\/$/, '')
    const normalizedPickerBase = /^https?:\/\//i.test(rawPickerBase)
      ? rawPickerBase
      : `https://${rawPickerBase}`
    const pickerUrl = `${normalizedPickerBase}/_layouts/15/FilePicker.aspx?${optionsQueryString}`

    form.setAttribute('action', pickerUrl)
    form.setAttribute('method', 'POST')
    form.setAttribute('target', 'filePicker')

    const input = document.createElement('input')
    input.setAttribute('type', 'hidden')
    input.setAttribute('name', 'access_token')
    input.setAttribute('value', authToken)

    form.appendChild(input)
    frame.appendChild(form)
    form.submit()
  }, [fetchToken, credentials, optionsQueryString])

  const closeSPModal: () => void = useCallback(() => {
    setFilesSelected([])
    cleanupPortAndCloseModal(port, closeModal, messageListener, setLoading)
  }, [port, closeModal, messageListener, setFilesSelected, setLoading])

  return {
    initializePicker,
    initializeMessageListener,
    closeSPModal,
  }
}
